export type ForwardNavigationIntentGate = {
  observeLocation: (location: string) => void;
  release: (target: string) => void;
  tryBegin: (target: string) => boolean;
};

/**
 * One identical forward intent may be pending from a location at a time.
 * The router—not a timeout—releases it by acknowledging a new location.
 */
export function createForwardNavigationIntentGate(): ForwardNavigationIntentGate {
  let location: string | null = null;
  const pending = new Set<string>();
  const intentKey = (target: string) => `${location ?? ""}\u0000${target}`;

  return {
    observeLocation(nextLocation) {
      if (location === nextLocation) return;
      location = nextLocation;
      pending.clear();
    },
    release(target) {
      pending.delete(intentKey(target));
    },
    tryBegin(target) {
      const key = intentKey(target);
      if (pending.has(key)) return false;
      pending.add(key);
      return true;
    }
  };
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, stableValue(entry)])
  );
}

export function navigationTargetKey(href: unknown): string {
  return typeof href === "string" ? href : JSON.stringify(stableValue(href));
}

function normalizedPathname(pathname: string): string {
  const path = pathname.split(/[?#]/, 1)[0] || "/";
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

export function navigationTargetMatchesLocation(
  href: unknown,
  location: string
): boolean {
  if (typeof href === "string") {
    return normalizedPathname(href) === normalizedPathname(location);
  }
  if (!href || typeof href !== "object") return false;
  const { pathname, params } = href as {
    pathname?: unknown;
    params?: Record<string, unknown>;
  };
  if (typeof pathname !== "string") return false;
  const resolved = pathname
    .replace(/\[\.\.\.([^\]]+)\]/g, (_match, key: string) => {
      const value = params?.[key];
      return Array.isArray(value) ? value.join("/") : String(value ?? "");
    })
    .replace(/\[([^\]]+)\]/g, (_match, key: string) =>
      String(params?.[key] ?? "")
    );
  return normalizedPathname(resolved) === normalizedPathname(location);
}
